import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import API from '../../services/api';
import { getUser } from '../../redux/user/actions';
import components from '../../components';
import { AccountsTab, AssetsTab, MappingsTab } from './tabs';

import './styles.scss';
import icons from '../../assets/icons';
import ToolBar from '../../components/toolbar';
import { getCmsInfo } from '../../utils/integrations';
import { getSelectedProject } from '../../redux/projects/actions';

const { CustomDropdown, Tabs } = components;

const IntegrationsContainer = ({ lastUpdated }) => {
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [openModal, setOpenModal] = useState(null);

  const user = useSelector(getUser);
  const project = useSelector(getSelectedProject);

  useEffect(() => {
    const fetchSites = async () => {
      const sites = await loadSites();
      setSites(sites || []);
      setLoading(false);
    };
    setLoading(true);
    fetchSites();
    loadIntegrations();
    loadCredentials();
    loadMappings();
    loadAssets();
  }, [user, lastUpdated, project]);

  useEffect(() => {
    loadCredentials();
    loadMappings();
  }, [selectedIntegration, project]);

  const loadIntegrations = () => {
    if (user) {
      setLoading(true);
      setIntegrations([]);
      setSelectedIntegration({});

      API.getIntegrations('', '', 200)
        .then(res => {
          const { integrations } = res;
          const integrationsList = integrations.map(integration => {
            return {
              ...integration,
              displayName: integration.vendor.charAt(0).toUpperCase() + integration.vendor.slice(1).toLowerCase()
            };
          });
          setIntegrations(integrationsList);
          if (integrations.length > 0) {
            setSelectedIntegration(integrations[0]);
          }
          setHasPermission(true);
        })
        .catch(error => {
          console.error(error);
          setHasPermission(false);
        })
        .finally(() => setLoading(false));
    }
  };

  const loadCredentials = () => {
    if (user && project && selectedIntegration) {
      setLoading(true);
      setMappings([]);

      const query = encodeURIComponent(`vendor = '${selectedIntegration?.vendor}'`);
      const filter = query !== '' ? query : '';
      API.getCredentials(project.id, '', filter, 200)
        .then(res => {
          setCredentials(res.credentials ? res.credentials : []);
          setHasPermission(true);
        })
        .catch(error => {
          console.error(error);
          setHasPermission(false);
        })
        .finally(() => setLoading(false));
    }
  };

  const loadMappings = async () => {
    if (selectedIntegration && selectedIntegration.vendor) {
      setLoading(true);
      const query = encodeURIComponent(`vendor = '${selectedIntegration?.vendor}'`);
      const filter = query !== '' ? query : '';
      if (user && project) {
        API.getMappings(project.id, '', filter, 200)
          .then(res => {
            setMappings(res.mappings);
            setHasPermission(true);
          })
          .catch(error => {
            console.error(error);
            setHasPermission(false);
          })
          .finally(() => setLoading(false));
      }
    }
  };

  const loadSites = async (filter = '', pageToken = '') => {
    if (project) {
      try {
        // change enpoint to getSitesByCompany
        const data = await API.getSites(project.id, pageToken, filter, 'displayName, id', 200);

        const sites = data.sites;
        const nextPageToken = data.nextPageToken;

        if (nextPageToken !== '') {
          const nextPageSites = await loadSites(filter, nextPageToken);
          return [...sites, ...nextPageSites];
        }

        return sites;
      } catch (error) {
        console.error('Error loding sites:', error);
      }
    }
  };

  const loadAssets = () => {
    if (!user || !project) return;

    setAssetsLoading(true);
    API.getAllAssets(project.id)
      .then(res => {
        setAssets(res.assets || []);
        setHasPermission(true);
      })
      .catch(error => {
        console.error(error);
        setHasPermission(false);
      })
      .finally(() => setAssetsLoading(false));
  };

  const onIntegrationSelected = selection => {
    const integration = getCmsInfo(selection, integrations);
    setSelectedIntegration(integration);
  };

  const mappingActionsDisabled =
    loading || !project?.enabledCmsIntegrations?.includes(selectedIntegration?.vendor);

  const toolbarButtons = [
    [
      {
        title: 'Add Asset',
        classes: 'btn-primary',
        disabled: assetsLoading || !project,
        handleClick: () => setOpenModal('asset')
      }
    ],
    [
      {
        title: 'Add Account',
        classes: 'btn-primary',
        disabled: loading || !project,
        handleClick: () => setOpenModal('account')
      }
    ],
    [
      {
        title: 'Import CSV',
        classes: 'btn-secondary',
        disabled: mappingActionsDisabled,
        handleClick: () => setOpenModal('import')
      },
      {
        title: 'Add CMS Mapping',
        classes: 'btn-primary',
        disabled: mappingActionsDisabled,
        handleClick: () => setOpenModal('mapping')
      }
    ]
  ];

  // Render empty state when User doesn't have any Integration
  if (!loading && (!selectedIntegration || integrations.length === 0)) {
    return (
      <div className="integrations-container">
        <div className="filter-bar">
          <div className="title">
            <img src={icons.integrations} alt="Integrations" />
            <h2>Integrations</h2>
          </div>
        </div>
        <div className="no-data-message flex-column">
          <h1>No Integrations found!</h1>
          <p>If you do have an active CMS Integration, please contact support </p>
        </div>
      </div>
    );
  }

  // Render denied state when User doesn't have permission
  if (!hasPermission) {
    return (
      <div className="integrations-container">
        <div className="filter-bar">
          <div className="title">
            <img src={icons.integrations} alt="Integrations" />
            <h2>Integrations</h2>
          </div>
        </div>
        <div className="no-data-message flex-column">
          <h1>{`You don't have the necessary permission to manage Integrations.`}</h1>
          <p>{`Please contact an administrator or support for further information.`}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="integrations-container">
      <ToolBar
        title={'Integrations'}
        icon={'integrations'}
        hasMultipleButtons
        buttonsList={toolbarButtons[selectedTab]}
        renderFilters={
          selectedTab > 0 ? (
            <CustomDropdown
              items={integrations}
              valueField="vendor"
              displayField="displayName"
              selectedItem={selectedIntegration?.vendor}
              handleSelection={event => onIntegrationSelected(event.target.value)}
              outline={false}
            />
          ) : null
        }
      />

      <Tabs
        selectedTab={selectedTab}
        onTabsChange={setSelectedTab}
        pages={{
          tabs: [
            {
              title: {
                name: `Assets (${assets.length})`,
                icon: 'deviceOutlineDark',
                description: 'Manage Assets and their Devices'
              },
              content: (
                <AssetsTab
                  assets={assets}
                  sites={sites}
                  loading={assetsLoading}
                  loadAssets={loadAssets}
                  isCreateModalOpen={openModal === 'asset'}
                  closeCreateModal={() => setOpenModal(null)}
                />
              )
            },
            {
              title: {
                name: `Accounts (${credentials.length})`,
                icon: 'userDark',
                description: 'Manage your account credentials'
              },
              content: (
                <AccountsTab
                  loading={loading}
                  loadCredentials={loadCredentials}
                  selectedIntegration={selectedIntegration}
                  credentials={credentials}
                  isCreateModalOpen={openModal === 'account'}
                  closeCreateModal={() => setOpenModal(null)}
                />
              )
            },
            {
              title: {
                name: `CMS Mappings (${mappings.length})`,
                icon: 'media',
                description: 'Bind assets to your players'
              },
              content: (
                <MappingsTab
                  credentials={credentials}
                  loading={loading}
                  assets={assets}
                  loadCredentials={loadCredentials}
                  loadMappings={loadMappings}
                  selectedIntegration={selectedIntegration}
                  mappings={mappings}
                  isCreateModalOpen={openModal === 'mapping'}
                  isImportModalOpen={openModal === 'import'}
                  closeModal={() => setOpenModal(null)}
                />
              )
            }
          ]
        }}
      />
    </div>
  );
};

export default IntegrationsContainer;
